"use client";

import React from "react";
import Image from "next/image";
import * as Dialog from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import Apple from "@/app/assets/icons/apple";
import GooglePlay from "@/app/assets/icons/googleplay";
import { DialogTitle } from "@/components/ui/dialog";
// import { handleAppDownload } from "@/app/utils/helper";
import { IMAGE_TYPE } from "@/app/enums/ui";
import { ProductType } from "@/types/product";
import { cn } from "@/lib/utils";
import { ICM_NAME_FULL } from "../utils/constants";

interface AppModalProps {
    open: boolean;
    onClose?: () => void;
    product?: ProductType;
}

const GetICMAppModal: React.FC<AppModalProps> = ({ open, onClose, product }) => {
    const isBroking = false && product; // will never be Broking

    return (
        <Dialog.Root open={open} onOpenChange={onClose} data-testid="app-download-dialog">
            <Dialog.Portal>
                <Dialog.Overlay
                    className="bg-gray-300/98 fixed inset-0 z-[998] backdrop-blur-lg"
                    data-testid="app-download-overlay"
                />
                <Dialog.Content
                    className="fixed bottom-0 left-0 right-0 z-[999] max-h-max w-full rounded-t-[24px] bg-white p-6 shadow-lg transition-all duration-300 ease-in-out md:bottom-auto md:left-1/2 md:top-1/2 md:h-[436px] md:w-[90%] md:min-w-[568px] md:max-w-md md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-[24px]"
                    data-testid="app-download-content"
                >
                    <VisuallyHidden.Root>
                        <DialogTitle data-testid="app-download-title"></DialogTitle>
                    </VisuallyHidden.Root>
                    <Dialog.Close asChild>
                        <button
                            className="absolute right-0 top-4 p-2 text-neutral-900 md:right-1 md:top-2"
                            onClick={onClose}
                            data-testid="app-download-close-btn"
                        >
                            <X size={28} />
                        </button>
                    </Dialog.Close>
                    <div
                        className="mt-0 flex flex-col gap-8 md:my-14"
                        data-testid="app-download-body"
                    >
                        <p
                            className="font-Semibold text-center text-lg leading-[130%] tracking-[-0.48px] text-neutral-900 md:text-4xl"
                            data-testid="app-download-heading"
                        >
                            Get the {ICM_NAME_FULL} app
                        </p>

                        <div
                            className={cn(
                                "flex h-[144px] w-full items-center justify-center gap-6 md:h-[222px]",
                                isBroking && "h-auto flex-col md:h-[222px] md:flex-row",
                            )}
                            data-testid="app-download-options"
                        >
                            <div
                                className={cn(
                                    "flex h-full min-w-full flex-col justify-between md:w-[200px] md:min-w-[200px]",
                                    isBroking ? "flex" : "hidden md:flex",
                                )}
                                data-testid="qr-code-section"
                            >
                                <p
                                    className="text-center text-lg font-normal leading-[164%] tracking-[-0.18px] text-neutral-300"
                                    data-testid="qr-code-text"
                                >
                                    Scan this QR code
                                </p>

                                <Image
                                    src={
                                        process.env.ICM_IMAGES_URL + IMAGE_TYPE.SVG + "/qr-code.svg"
                                    }
                                    alt="qr-code"
                                    height={158}
                                    width={158}
                                    className="m-auto mt-8 h-[132px] w-[132px] rounded-md md:h-[158px] md:w-[158px]"
                                    data-testid="qr-code-image2"
                                />
                            </div>
                            {isBroking ? (
                                <div
                                    className="my-2 w-full border-t-2 border-dashed border-gray-500 md:hidden"
                                    data-testid="divider-mobile"
                                    aria-hidden
                                />
                            ) : null}
                            <div
                                className="hidden h-[144px] border-l-2 border-dashed border-gray-500 md:block md:h-[222px]"
                                data-testid="divider"
                            />
                            <div
                                className="flex h-full min-w-full flex-col justify-between md:w-[240px] md:min-w-[240px]"
                                data-testid="download-buttons-section"
                            >
                                <p
                                    className={cn(
                                        "text-center text-lg font-normal leading-[164%] tracking-[-0.18px] text-neutral-300",
                                        isBroking ? "mb-4 block md:mb-6" : "mb-6 hidden md:block",
                                    )}
                                    data-testid="download-app-text"
                                >
                                    Or, download the mobile app
                                </p>
                                <div
                                    className="flex min-w-full flex-col items-center gap-3 md:hidden"
                                    data-testid="app-store-buttons-mobile"
                                >
                                    <RegularButton
                                        showRightIcon={false}
                                        icon={<Apple />}
                                        size={ButtonSize.Small}
                                        variant={ButtonVariant.SecondaryDefault}
                                        className="w-full max-w-[220px] bg-white"
                                        label={"iOS"}
                                        onClick={() => window.open(process.env.ICM_APPLE_APP_URL)}
                                        data-testid="ios-download-btn-mobile"
                                    />
                                    <RegularButton
                                        showRightIcon={false}
                                        icon={<GooglePlay />}
                                        size={ButtonSize.Small}
                                        variant={ButtonVariant.SecondaryDefault}
                                        className="w-full max-w-[220px] bg-white"
                                        label={"Android"}
                                        onClick={() => window.open(process.env.ICM_PLAYSTORE_URL)}
                                        data-testid="android-download-btn-mobile"
                                    />
                                </div>
                                <div
                                    className="hidden min-w-full flex-col items-center gap-4 md:flex"
                                    data-testid="app-store-buttons"
                                >
                                    <RegularButton
                                        showRightIcon={false}
                                        icon={<Apple />}
                                        size={ButtonSize.Large}
                                        variant={ButtonVariant.SecondaryDefault}
                                        className="w-full min-w-full bg-white"
                                        label={"iOS"}
                                        onClick={() => window.open(process.env.ICM_APPLE_APP_URL)}
                                        data-testid="ios-download-btn"
                                    />
                                    <RegularButton
                                        showRightIcon={false}
                                        icon={<GooglePlay />}
                                        size={ButtonSize.Large}
                                        variant={ButtonVariant.SecondaryDefault}
                                        className="w-full min-w-full bg-white"
                                        label={"Android"}
                                        onClick={() => window.open(process.env.ICM_PLAYSTORE_URL)}
                                        data-testid="android-download-btn"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default GetICMAppModal;
